import { ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

interface DraggableGridProps<T> {
  items: T[];
  onReorder: (reorderedItems: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  getItemId: (item: T, index: number) => string;
  gridClassName?: string;
  sortingStrategy?: 'vertical' | 'rect';
  children?: ReactNode; // For additional non-sortable items like "Add" buttons
}

/**
 * A reusable drag-and-drop grid component that handles all the DnD logic.
 *
 * @example
 * ```tsx
 * <DraggableGrid
 *   items={diagnoses}
 *   onReorder={handleReorder}
 *   renderItem={(dx, idx) => (
 *     <SortableDiagnosisCard
 *       diagnosis={dx}
 *       position={idx + 1}
 *       onEdit={() => handleEdit(idx)}
 *     />
 *   )}
 *   getItemId={(_, idx) => `diagnosis-${idx}`}
 *   gridClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
 * >
 *   <AddButton />
 * </DraggableGrid>
 * ```
 */
export function DraggableGrid<T>({
  items,
  onReorder,
  renderItem,
  getItemId,
  gridClassName = 'grid gap-4',
  sortingStrategy = 'rect',
  children,
}: DraggableGridProps<T>) {
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(
      (item, idx) => getItemId(item, idx) === active.id
    );
    const newIndex = items.findIndex(
      (item, idx) => getItemId(item, idx) === over.id
    );

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    onReorder(reorderedItems);
  };

  const strategy =
    sortingStrategy === 'vertical'
      ? verticalListSortingStrategy
      : rectSortingStrategy;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item, idx) => getItemId(item, idx))}
        strategy={strategy}
      >
        <div className={gridClassName}>
          {items.map((item, idx) => renderItem(item, idx))}
          {children}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default DraggableGrid;
